/** @jsx jsx */
import React, { useEffect, useState, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { jsx, css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Grid,
  Fab,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  CircularProgress,
  DialogActions,
  Box,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { ICustomTheme } from 'interfaces';
import { Title, TertiaryButton, ErrorBoundary } from 'components';
import PhotoIcon from 'assets/icons/photo';
import FileUploadedIcon from 'assets/icons/fileUploaded';
import { STEPS } from '../constants';
import { CustomPreviewIcon } from 'assets/icons/CustomPreviewIcon';
import { CustomEditIcon } from 'assets/icons/CustomEditIcon';

const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpeg,.docx,.xlsx,.csv,.gif,.bmp';

const FILE_TYPES = {
  png: 'image/png',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  gif: 'image/gif',
  bmp: 'image/bmp',
} as const;

const PanelContainer = styled(Box)(({ theme }: { theme: ICustomTheme }) => ({
  border: `10px solid ${theme.colors.white}`,
  outline: `1px solid ${theme.colors.paletteFromBO.primary}`,
  width: 360,
  [theme.breakpoints.down('sm')]: {
    width: 330,
  },
}));

const PDFDocument = styled(Document)({
  canvas: {
    height: 'auto !important',
    margin: '0 auto',
    width: '100% !important',
  },
});

const ImagePreview = styled('img')({
  width: '100%',
});

export const StyledCircularProgress = styled(CircularProgress)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
}));

const UnavailablePreview = styled(Box)(
  ({ theme }: { theme: ICustomTheme }) => ({
    background: theme.colors.grey.light3,
    padding: theme.spacing(6, 3),
    textAlign: 'center',
  }),
);

const UnavailablePreviewFileName = styled('span')(
  ({ theme }: { theme: ICustomTheme }) => ({
    display: 'block',
    fontWeight: 700,
    margin: theme.spacing(1, 0),
    overflowWrap: 'anywhere',
  }),
);

const UnavailablePreviewHint = styled('span')(
  ({ theme }: { theme: ICustomTheme }) => ({
    color: theme.colors.grey.main,
    display: 'block',
  }),
);

function Preview({
  filePreviewURL,
  fileType,
  fileName,
}: {
  filePreviewURL: string;
  fileType: string;
  fileName: string;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const logPreviewError = (error: Error): void => {
    // eslint-disable-next-line no-console
    console.error('PDF preview error:', error);
  };

  const unavailablePreview = (
    <UnavailablePreview>
      <Title color={theme.colors.paletteFromBO.primary} align="center">
        {t('challenges.declaration.proof.previewUnavailable')}
      </Title>
      <UnavailablePreviewFileName>{fileName}</UnavailablePreviewFileName>
      <UnavailablePreviewHint>
        {t('challenges.declaration.proof.previewUnavailableHint')}
      </UnavailablePreviewHint>
    </UnavailablePreview>
  );

  return fileType === FILE_TYPES.pdf ? (
    // L'aperçu peut échouer (worker pdf.js bloqué par un adblocker, PDF illisible) alors que le
    // fichier est bien joint : on le rappelle par son nom pour ne pas laisser croire à un échec d'envoi.
    // Isole le rendu du PDF pour qu'une erreur d'aperçu ne fasse pas planter toute la déclaration (page blanche).
    // `error` couvre l'échec de chargement (que la boundary ne voit pas), la boundary couvre l'exception au rendu.
    <ErrorBoundary key={filePreviewURL} fallback={unavailablePreview}>
      <PDFDocument
        loading={
          <div css={{ textAlign: 'center' }}>
            <StyledCircularProgress size={50} />
          </div>
        }
        error={unavailablePreview}
        onSourceError={logPreviewError}
        onLoadError={logPreviewError}
        file={filePreviewURL}
      >
        <Page pageNumber={1} />
      </PDFDocument>
    </ErrorBoundary>
  ) : (
    <ImagePreview
      src={filePreviewURL}
      alt={t('challenges.declaration.proof.previewClose')}
    />
  );
}

const NoPreviewContainer = styled(Box)(
  ({ theme }: { theme: ICustomTheme }) => ({
    background: theme.colors.grey.light3,
    padding: theme.spacing(18, 3),
  }),
);

const cssPanelIcon = (isAFileUploaded: boolean) => (theme: ICustomTheme) =>
  css({
    color: isAFileUploaded
      ? theme.colors.grey.main
      : theme.colors.paletteFromBO.primary,
    height: isAFileUploaded ? 117 : 60,
    margin: `0 auto ${theme.spacing(isAFileUploaded ? 3 : 1)}px auto`,
    width: isAFileUploaded ? 90 : 74,
  });

function Panel({
  isPreviewAvailable,
  filePreviewURL,
  file,
  openFilePicker,
  isAFileUploaded,
}: {
  isPreviewAvailable: boolean;
  filePreviewURL?: string;
  file?: File;
  openFilePicker: () => void;
  isAFileUploaded: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const Icon = isAFileUploaded ? FileUploadedIcon : PhotoIcon;

  return (
    <Button
      onClick={openFilePicker}
      aria-label={t('challenges.declaration.proof.filePickerLabel')}
      disabled={isAFileUploaded}
    >
      <PanelContainer>
        {filePreviewURL && isPreviewAvailable && file ? (
          <Preview
            filePreviewURL={filePreviewURL}
            fileType={file.type}
            fileName={file.name}
          />
        ) : (
          <NoPreviewContainer>
            <Icon css={cssPanelIcon(isAFileUploaded)} />
            <Title color={theme.colors.paletteFromBO.primary} align="center">
              {t(
                `challenges.declaration.proof.${
                  isAFileUploaded ? 'filePickerSuccess' : 'filePicker'
                }`,
              )}
            </Title>
          </NoPreviewContainer>
        )}
      </PanelContainer>
    </Button>
  );
}

const Action = styled(Fab)(
  ({ theme, iconType }: { theme: ICustomTheme; iconType: string }) => ({
    margin: theme.spacing(1.5, 1),
    backgroundColor:
      iconType === 'edit'
        ? theme.colors.paletteFromBO.primary
        : theme.colors.paletteFromBO.main,
  }),
);

function Actions({
  openFilePicker,
  openFilePreview,
}: {
  openFilePicker: () => void;
  openFilePreview?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  return (
    <Grid container justify="center">
      {openFilePreview && (
        <Grid item>
          <Action
            onClick={openFilePreview}
            aria-label={t('challenges.declaration.proof.preview')}
            iconType={'preview'}
          >
            <CustomPreviewIcon color={theme.colors.white} />
          </Action>
        </Grid>
      )}
      <Grid item>
        <Action
          onClick={openFilePicker}
          iconType={'edit'}
          aria-label={t('challenges.declaration.proof.edit')}
        >
          <CustomEditIcon color={theme.colors.white} />
        </Action>
      </Grid>
    </Grid>
  );
}

const DialogContainer = styled(Dialog)(
  ({ theme }: { theme: ICustomTheme }) => ({
    '.MuiDialog-paper': {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
    },
  }),
);

function PreviewDialog({
  isOpen,
  handleClose,
  file,
  filePreviewURL,
}: {
  isOpen: boolean;
  handleClose: () => void;
  file?: File;
  filePreviewURL?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  return (
    <DialogContainer
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={isOpen}
    >
      <IconButton css={{ alignSelf: 'flex-end' }} onClick={handleClose}>
        <CloseIcon fontSize="large" />
      </IconButton>
      <DialogTitle>
        <Title
          textCase="upper"
          size="h4"
          css={{ fontWeight: 800, color: theme.colors.paletteFromBO.main }}
        >
          {t('challenges.declaration.proof.previewTitle')}
        </Title>
      </DialogTitle>
      <DialogContent
        css={(theme: ICustomTheme) => ({
          margin: theme.spacing(2, 0),
          overflowX: 'hidden',
          padding: 0,
          width: '70%',
        })}
      >
        {filePreviewURL && file && (
          <Preview
            filePreviewURL={filePreviewURL}
            fileType={file.type}
            fileName={file.name}
          />
        )}
      </DialogContent>
      <DialogActions>
        <TertiaryButton onClick={handleClose}>
          {t('challenges.declaration.proof.previewClose')}
        </TertiaryButton>
      </DialogActions>
    </DialogContainer>
  );
}

const InvisibleInput = styled.input({
  display: 'none',
});

export default function FilePicker({
  isAFileUploaded,
  file,
  setFile,
}: {
  isAFileUploaded: boolean;
  file?: File;
  setFile: (file: File) => void;
}): JSX.Element {
  const [isFilePreviewOpen, setIsFilePreviewOpen] = useState(false);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [fileUrl, setFileUrl] = useState('');

  const openFilePicker = (): void => inputFile?.current?.click();
  function handleFileUpload(event: ChangeEvent<HTMLInputElement>): void {
    const file = event?.target?.files?.[0];

    if (file) {
      setFile(file);
    }
  }

  const openFilePreview = (): void => setIsFilePreviewOpen(true);
  const closeFilePreview = (): void => setIsFilePreviewOpen(false);

  useEffect(() => {
    if (!file) {
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setFileUrl(objectUrl);

    // eslint-disable-next-line consistent-return
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file, setFileUrl]);

  const isPreviewAvailable =
    file?.type === FILE_TYPES.pdf ||
    file?.type === FILE_TYPES.png ||
    file?.type === FILE_TYPES.jpg ||
    file?.type === FILE_TYPES.gif ||
    file?.type === FILE_TYPES.bmp;

  return (
    <React.Fragment>
      <Panel
        file={file}
        isPreviewAvailable={isPreviewAvailable}
        filePreviewURL={fileUrl}
        openFilePicker={openFilePicker}
        isAFileUploaded={isAFileUploaded}
      />
      {isAFileUploaded && (
        <Actions
          openFilePreview={isPreviewAvailable ? openFilePreview : undefined}
          openFilePicker={openFilePicker}
        />
      )}
      <PreviewDialog
        isOpen={isFilePreviewOpen}
        handleClose={closeFilePreview}
        file={file}
        filePreviewURL={fileUrl}
      />
      <InvisibleInput
        name={STEPS.file}
        aria-hidden
        type="file"
        ref={inputFile}
        onChange={handleFileUpload}
        accept={ACCEPTED_FILE_TYPES}
      />
    </React.Fragment>
  );
}
